import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Filter as FilterIcon, Play, SlidersHorizontal, Tags } from 'lucide-react';
import type { ProductFilterToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useProductFiltersList, useProductFilterMutations } from '~/hooks/use-product-filters';
import { formatDateTime } from '~/lib/format-date';
import type { FilterNodeValue } from '~/lib/filters/registry';
import { AssignmentsDialog } from './assignments-dialog';
import { FilterBuilder } from './filter-builder';

interface FormState {
	id?: number;
	Name: string;
	Description: string;
	ShouldRunManually: boolean;
}

export function ProductFiltersGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useProductFiltersList();
	const { create, update, setFilter, runManually, remove } = useProductFilterMutations();

	const [editRow, setEditRow] = useState<FormState | null>(null);
	const [deleteRow, setDeleteRow] = useState<ProductFilterToken | null>(null);
	const [builderRow, setBuilderRow] = useState<ProductFilterToken | null>(null);
	const [draftFilter, setDraftFilter] = useState<FilterNodeValue>({});
	const [assignmentsRow, setAssignmentsRow] = useState<ProductFilterToken | null>(null);

	const columns = useMemo<SystemGridColumn<ProductFilterToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.filters.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: f => f.Name,
				exportValue: f => f.Name,
				render: f => <span className="truncate text-sm font-medium text-foreground">{f.Name}</span>
			},
			{
				key: 'description',
				header: t('catalog.filters.columns.description'),
				width: 'minmax(0,1.5fr)',
				sortValue: f => f.Description ?? '',
				exportValue: f => f.Description ?? '',
				render: f => <span className="truncate text-sm text-foreground-muted">{f.Description}</span>
			},
			{
				key: 'manualRun',
				header: t('catalog.filters.columns.manualRun'),
				width: '180px',
				sortValue: f => (f.ShouldRunManually ? String(f.LastManuallyRun ?? '0') : ''),
				exportValue: f => (f.ShouldRunManually ? formatDateTime(f.LastManuallyRun) || t('common.yes') : ''),
				render: f =>
					f.ShouldRunManually ? (
						<span className="truncate text-xs text-foreground-muted">
							{f.LastManuallyRun ? formatDateTime(f.LastManuallyRun) : t('catalog.filters.neverRun')}
						</span>
					) : null
			}
		],
		[t]
	);

	const saveDetails = () => {
		if (!editRow) return;
		const existing = data?.find(f => f.id === editRow.id);
		const token: ProductFilterToken = {
			...existing,
			Name: editRow.Name.trim(),
			Description: editRow.Description || null,
			ShouldRunManually: editRow.ShouldRunManually
		};
		const mutation = editRow.id ? update : create;
		// New filters must be visible (server GetAll returns only IsVisible rows) and start with an empty definition.
		if (!editRow.id) {
			token.Filter = {};
			token.iv = true;
		}
		mutation.mutate(token, {
			onSuccess: () => {
				toast.success(t('common.save'));
				setEditRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const saveFilter = () => {
		if (!builderRow) return;
		setFilter.mutate(
			{ ...builderRow, Filter: draftFilter },
			{
				onSuccess: () => {
					toast.success(t('common.save'));
					setBuilderRow(null);
				},
				onError: err => toast.error(serverMessage(err))
			}
		);
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ProductFilterToken>
				gridId="product-filters"
				title={t('catalog.filters.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={f => f.id ?? f.Name ?? ''}
				columns={columns}
				searchPredicate={(f, q) => (f.Name ?? '').toLowerCase().includes(q) || (f.Description ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<FilterIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.filters.add')}
				onAdd={() => setEditRow({ Name: '', Description: '', ShouldRunManually: false })}
				onEdit={f => setEditRow({ id: f.id, Name: f.Name ?? '', Description: f.Description ?? '', ShouldRunManually: Boolean(f.ShouldRunManually) })}
				onDelete={setDeleteRow}
				// edit + delete + definition + assignments (+ run for manual filters)
				actionsWidth={180}
				rowActions={f => (
					<>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7"
							title={t('catalog.filters.editDefinition')}
							onClick={() => {
								setBuilderRow(f);
								setDraftFilter((f.Filter as FilterNodeValue) ?? {});
							}}>
							<SlidersHorizontal size={15} />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7"
							title={t('catalog.filters.assignments.edit')}
							onClick={() => setAssignmentsRow(f)}>
							<Tags size={15} />
						</Button>
						{f.ShouldRunManually && (
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								title={t('catalog.filters.run')}
								disabled={runManually.isPending}
								onClick={() =>
									f.id &&
									runManually.mutate(f.id, {
										onSuccess: () => toast.success(t('catalog.filters.runDone')),
										onError: err => toast.error(serverMessage(err))
									})
								}>
								<Play size={15} />
							</Button>
						)}
					</>
				)}
				exportFilename="product-filters"
			/>

			{/* Name / Description dialog */}
			<Dialog open={Boolean(editRow)} onOpenChange={open => !open && setEditRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editRow?.id ? t('catalog.filters.editTitle') : t('catalog.filters.addTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.filters.formSubtitle')}</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="pf-name">{t('catalog.filters.columns.name')}</Label>
							<Input
								id="pf-name"
								value={editRow?.Name ?? ''}
								maxLength={60}
								onChange={e => setEditRow(r => (r ? { ...r, Name: e.target.value } : r))}
								autoFocus
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="pf-desc">{t('catalog.filters.columns.description')}</Label>
							<Textarea
								id="pf-desc"
								value={editRow?.Description ?? ''}
								maxLength={500}
								onChange={e => setEditRow(r => (r ? { ...r, Description: e.target.value } : r))}
							/>
						</div>
						<div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2">
							<div className="flex flex-col">
								<Label htmlFor="pf-manual">{t('catalog.filters.manualRun')}</Label>
								<span className="text-xs text-foreground-muted">{t('catalog.filters.manualRunHelp')}</span>
							</div>
							<Switch
								id="pf-manual"
								checked={editRow?.ShouldRunManually ?? false}
								onCheckedChange={v => setEditRow(r => (r ? { ...r, ShouldRunManually: v } : r))}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setEditRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button onClick={saveDetails} disabled={!editRow?.Name.trim() || create.isPending || update.isPending}>
							{t('common.save')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Dynamic-filter builder */}
			<Dialog open={Boolean(builderRow)} onOpenChange={open => !open && setBuilderRow(null)}>
				<DialogContent className="max-h-[88vh] gap-0 overflow-hidden p-0 sm:max-w-4xl">
					<DialogHeader className="border-b border-border p-5">
						<DialogTitle>{t('catalog.filters.builderTitle', { name: builderRow?.Name ?? '' })}</DialogTitle>
						<DialogDescription>{t('catalog.filters.builderSubtitle')}</DialogDescription>
					</DialogHeader>
					<div className="max-h-[60vh] overflow-auto p-5">
						<FilterBuilder value={draftFilter} onChange={setDraftFilter} />
					</div>
					<DialogFooter className="border-t border-border p-4">
						<Button variant="ghost" onClick={() => setBuilderRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button onClick={saveFilter} disabled={setFilter.isPending}>
							{t('common.save')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AssignmentsDialog filter={assignmentsRow} onClose={() => setAssignmentsRow(null)} />

			{/* Delete confirm */}
			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.filters.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.filters.deleteBody', { name: deleteRow?.Name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
